import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Sparkles, ArrowRight, Paperclip } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "../components/FileUpload";

const URG_COLOR = { Alta: "#dc2626", "Média": "#eab308", Baixa: "#16a34a" };
const STATUS_COLOR = { aberto: "#dc2626", em_atendimento: "#eab308", resolvido: "#3b82f6", fechado: "#16a34a" };

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cliente_id: "", unidade_id: "", tipo: "manutencao", descricao: "" });
  const [detail, setDetail] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, c, u] = await Promise.all([
      api.get("/tickets"),
      api.get("/clientes"),
      api.get("/unidades"),
    ]);
    setTickets(t.data);
    setClientes(c.data);
    setUnidades(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.cliente_id || !form.descricao) {
      toast.error("Cliente e descrição obrigatórios");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, unidade_id: form.unidade_id || null };
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket criado · IA: ${data.urgencia_ia}`);
      setOpen(false);
      setForm({ cliente_id: "", unidade_id: "", tipo: "manutencao", descricao: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally {
      setBusy(false);
    }
  };

  const abrir = async (t) => {
    setDetail(t);
    const { data } = await api.get(`/tickets/${t.id}/updates`);
    setUpdates(data);
  };

  const mudarStatus = async (t, status) => {
    try {
      await api.post(`/tickets/${t.id}/updates`, { status_novo: status, observacao: `Status alterado para ${status}` });
      toast.success("Status atualizado");
      load();
      abrir({ ...t, status });
    } catch {
      toast.error("Falha");
    }
  };

  const STATUS_NEXT = {
    aberto: "em_atendimento",
    em_atendimento: "resolvido",
    resolvido: "fechado",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Tickets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Triagem por IA Claude · ordenados por urgência
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ticket-btn" className="text-white" style={{ background: "#F97316" }}>
              <Plus size={14} className="mr-2" /> Abrir ticket
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Abrir ticket <Badge variant="secondary"><Sparkles size={10} className="mr-1" />triagem IA automática</Badge>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Cliente</Label>
                <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                  <SelectTrigger data-testid="ticket-cliente"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => (<SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unidade (opcional)</Label>
                <Select value={form.unidade_id} onValueChange={(v) => setForm({ ...form, unidade_id: v })}>
                  <SelectTrigger data-testid="ticket-unidade"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {unidades.map((u) => (<SelectItem key={u.id} value={u.id}>{u.numero_serie}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="troca">Troca</SelectItem>
                    <SelectItem value="manutencao">Manutenção</SelectItem>
                    <SelectItem value="devolucao">Devolução</SelectItem>
                    <SelectItem value="reclamacao">Reclamação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descrição do problema</Label>
                <Textarea data-testid="ticket-descricao" rows={4} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              </div>
              <Button data-testid="ticket-submit" onClick={submit} disabled={busy} className="w-full text-white" style={{ background: "#1B3A6B" }}>
                {busy ? "Enviando..." : "Criar com triagem IA"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Urgência IA</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((t) => (
              <TableRow key={t.id} data-testid={`ticket-row-${t.id}`}>
                <TableCell><Badge variant="outline">{t.tipo}</Badge></TableCell>
                <TableCell className="max-w-[360px] truncate text-sm">{t.descricao}</TableCell>
                <TableCell className="text-center">
                  <Badge style={{ background: URG_COLOR[t.urgencia_ia] || "#6b7280", color: "white" }}>
                    {t.urgencia_ia || "—"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge style={{ background: STATUS_COLOR[t.status], color: "white" }}>{t.status}</Badge>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => abrir(t)}>Detalhes</Button>
                  {STATUS_NEXT[t.status] && (
                    <Button data-testid={`avancar-${t.id}`} size="sm" className="text-white" style={{ background: "#F97316" }} onClick={() => mudarStatus(t, STATUS_NEXT[t.status])}>
                      <ArrowRight size={12} className="mr-1" />
                      {STATUS_NEXT[t.status]}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Ticket #{detail?.id?.slice(0, 8)}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4">
              <Card className="p-4 bg-slate-50">
                <div className="space-y-2 text-sm">
                  <div><strong>Tipo:</strong> {detail.tipo}</div>
                  <div><strong>Status:</strong> <Badge style={{ background: STATUS_COLOR[detail.status], color: "white" }}>{detail.status}</Badge></div>
                  <div><strong>Descrição:</strong> {detail.descricao}</div>
                </div>
              </Card>
              <Card className="p-4">
                <h4 className="font-bold mb-2 flex items-center gap-2"><Paperclip size={14}/>Anexar arquivo</h4>
                <FileUpload
                  endpoint={`/api/tickets/${detail.id}/arquivo`}
                  accept="image/*,application/pdf,video/mp4"
                  maxSizeMB={20}
                  currentUrl={detail.foto_url}
                  onChange={(url) => { setDetail({...detail, foto_url: url}); load(); }}
                  testId={`ticket-file-${detail.id}`}
                  label="Foto/PDF/Vídeo"
                />
              </Card>
              <Card className="p-4 border-l-4" style={{ borderLeftColor: "#F97316" }}>
                <h4 className="font-bold mb-2 flex items-center gap-1" style={{ color: "#F97316" }}><Sparkles size={14} />Triagem IA</h4>
                <div className="text-sm space-y-1">
                  <div><strong>Urgência:</strong> {detail.urgencia_ia}</div>
                  <div><strong>Tipo identificado:</strong> {detail.tipo_ia}</div>
                  <div><strong>Ação recomendada:</strong> {detail.acao_ia}</div>
                </div>
              </Card>
              <div>
                <h4 className="font-bold mb-2" style={{ color: "#1B3A6B" }}>Histórico</h4>
                {updates.map((u) => (
                  <div key={u.id} className="border-l-2 pl-3 pb-3" style={{ borderColor: "#F97316" }}>
                    <div className="text-xs text-muted-foreground">{new Date(u.data).toLocaleString("pt-BR")}</div>
                    <div className="text-sm"><Badge variant="outline" className="text-[10px] mr-1">{u.status_anterior} → {u.status_novo}</Badge></div>
                    <div className="text-xs text-muted-foreground mt-1">{u.observacao}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
